import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  HomeChartContainer,
  HomeChartWrapper,
  FirstChart,
  SecondChart,
  ChartContent,
  ChartAmount,
  ChartPrice,
  ChartSpan,
  ChartData,
} from "./chart.styles";
import { colors } from "../../../../theme/color";
import { LuCalendarDays } from "react-icons/lu";
import {
  FlyoutMenu,
  CalendarText,
  YearFilter,
  DropDownIcon,
  DropDownMenuList,
  DropDownMenu,
} from "../../../../pages/Dashboard/Dashboard.styles";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { GET_TRANSACTION_BY_ORGANIZATION_ID } from "../../../../graphql/queries/transaction";

// Define necessary types
interface Transaction {
  updatedAt: string;
  quantity: number;
  price: number;
  amount: number;
}

interface MonthlyData {
  name: string;
  uv: number;
  pv: number;
  amt: number;
}

export const initialData: MonthlyData[] = [
  { name: "Jan", uv: 0, pv: 0, amt: 0 },
  { name: "Feb", uv: 0, pv: 0, amt: 0 },
  { name: "Mar", uv: 0, pv: 0, amt: 0 },
  { name: "Apr", uv: 0, pv: 0, amt: 0 },
  { name: "May", uv: 0, pv: 0, amt: 0 },
  { name: "Jun", uv: 0, pv: 0, amt: 0 },
  { name: "Jul", uv: 0, pv: 0, amt: 0 },
  { name: "Aug", uv: 0, pv: 0, amt: 0 },
  { name: "Sep", uv: 0, pv: 0, amt: 0 },
  { name: "Oct", uv: 0, pv: 0, amt: 0 },
  { name: "Nov", uv: 0, pv: 0, amt: 0 },
  { name: "Dec", uv: 0, pv: 0, amt: 0 },
];

const Index = () => {
  const [isDropDownGross, setIsDropDownGross] = useState(false);
  const [isDropDownNet, setIsDropDownNet] = useState(false);
  const [selectedYearGross, setSelectedYearGross] = useState("2023");
  const [selectedYearNet, setSelectedYearNet] = useState("2023");
  const [selectedIndexGross, setSelectedIndexGross] = useState(0);
  const [selectedIndexNet, setSelectedIndexNet] = useState(0);
  const [dataTrans, setDataTrans] = useState(initialData);
  const years = ["2023", "2024", "2025", "2026"];

  const {
    loading,
    error,
    data: queryData,
  } = useQuery(GET_TRANSACTION_BY_ORGANIZATION_ID, {
    variables: {
      payload: {
        limit: 10,
        page: 1,
      },
    },
  });

  const transactions = queryData?.getAllTransactionsByOrganizationId?.data;

  useEffect(() => {
    if (queryData && queryData.getAllTransactionsByOrganizationId) {
      const transactions: Transaction[] =
        queryData.getAllTransactionsByOrganizationId.data;
      const monthlyData = initialData.map((month, index) => {
        const monthlyTransactions = transactions.filter((transaction) => {
          const transactionDate = new Date(parseInt(transaction.updatedAt));
          return (
            transactionDate.getMonth() === index &&
            transactionDate.getFullYear().toString() === selectedYearGross
          );
        });

        const totalAmt = monthlyTransactions.reduce(
          (sum, trans) => sum + trans.amount,
          0
        );

        return { ...month, amt: totalAmt };
      });
      setDataTrans(monthlyData);
    }
  }, [queryData, selectedYearGross]);

  const handleDropDownMenuGross = () => {
    setIsDropDownGross(!isDropDownGross);
  };

  const handleDropDownMenuNet = () => {
    setIsDropDownNet(!isDropDownNet);
  };

  const handleSelectedIndexGross = (index: number) => {
    setSelectedIndexGross(index);
    setSelectedYearGross(years[index]);
    setIsDropDownGross(!isDropDownGross);
  };

  const handleSelectedIndexNet = (index: number) => {
    setSelectedIndexNet(index);
    setSelectedYearNet(years[index]);
    setIsDropDownNet(!isDropDownNet);
  };

  return (
    <HomeChartContainer>
      <HomeChartWrapper>
        <FirstChart>
          <ChartContent>
            <div>
              <ChartSpan>Gross Volume</ChartSpan>
            </div>
            <div>
              <FlyoutMenu>
                <YearFilter onClick={handleDropDownMenuGross}>
                  <LuCalendarDays
                    color={colors.ButtonBackgroundColors.primaryDefaultButton}
                    size={"20px"}
                  />
                  <CalendarText>Year {selectedYearGross} </CalendarText>
                  <DropDownIcon
                    src="/assets/icons/Drop_Down.png"
                    onClick={handleDropDownMenuGross}
                  />
                </YearFilter>

                <DropDownMenu isDropDown={isDropDownGross}>
                  {years.map((year, index: number) => (
                    <DropDownMenuList
                      selected={selectedIndexGross === index}
                      key={year}
                      onClick={() => handleSelectedIndexGross(index)}
                    >
                      {year}
                    </DropDownMenuList>
                  ))}
                </DropDownMenu>
              </FlyoutMenu>
            </div>
          </ChartContent>
          <ChartAmount>
            <ChartPrice>
              ${dataTrans.reduce((sum, month) => sum + month.amt, 0).toFixed(2)}
            </ChartPrice>
          </ChartAmount>
          <ChartData>
            <ResponsiveContainer width="100%" height={300} className="mobile">
              <AreaChart
                data={dataTrans}
                margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient id="areaColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#09005B" />
                    <stop offset="48.25%" stopColor="#F0EFFF" />
                    <stop offset="103.37%" stopColor="#FFFFFF" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3" vertical={false} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#5E5E5E", fontSize: 12 }}
                />
                <YAxis
                  domain={[0, Math.max(...dataTrans.map((month) => month.amt))]}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `$ ${value}`}
                  tick={{ fill: "#5E5E5E", fontSize: 12 }}
                />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="amt"
                  stroke="#09005B"
                  fill="url(#areaColor)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartData>
        </FirstChart>
        <SecondChart>
          <ChartContent>
            <div>
              <ChartSpan>Net Volume from Sales</ChartSpan>
            </div>
            <div>
              <FlyoutMenu>
                <YearFilter onClick={handleDropDownMenuNet}>
                  <LuCalendarDays
                    color={colors.ButtonBackgroundColors.primaryDefaultButton}
                    size={"20px"}
                  />
                  <CalendarText>Year {selectedYearNet} </CalendarText>
                  <DropDownIcon
                    src="/assets/icons/Drop_Down.png"
                    onClick={handleDropDownMenuNet}
                  />
                </YearFilter>

                <DropDownMenu isDropDown={isDropDownNet}>
                  {years.map((year, index) => (
                    <DropDownMenuList
                      selected={selectedIndexNet === index}
                      key={year}
                      onClick={() => handleSelectedIndexNet(index)}
                    >
                      {year}
                    </DropDownMenuList>
                  ))}
                </DropDownMenu>
              </FlyoutMenu>
            </div>
          </ChartContent>
          <ChartAmount>
            <ChartPrice>$0.00</ChartPrice>
          </ChartAmount>
          <ChartData>
            <ResponsiveContainer width="100%" height={300} className="mobile">
              <AreaChart
                data={initialData}
                margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient id="areaColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#09005B" />
                    <stop offset="48.25%" stopColor="#F0EFFF" />
                    <stop offset="103.37%" stopColor="#FFFFFF" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3" vertical={false} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#5E5E5E", fontSize: 12 }}
                />
                <YAxis
                  domain={[0, 250]}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `$ ${value}`}
                  tick={{ fill: "#5E5E5E", fontSize: 12 }}
                />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="uv"
                  stroke="#09005B"
                  fill="url(#areaColor)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartData>
        </SecondChart>
      </HomeChartWrapper>
    </HomeChartContainer>
  );
};

export default Index;
